import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

const ContextApi = createContext();

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;

async function postJson(url) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: "{}",
  });
  if (!res.ok) throw new Error(res.statusText);
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const ContextProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshPromiseRef = useRef(null);

  const logout = useCallback(() => {
    fetch(`${AUTH_API_URL}/auth/public/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: "{}",
    }).catch(() => {});
    localStorage.removeItem("USER");
    localStorage.removeItem("CSRF_TOKEN");
    localStorage.removeItem("IS_ADMIN");
    setToken(null);
    setCurrentUser(null);
    setIsAdmin(false);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = postJson(`${AUTH_API_URL}/auth/public/refresh`)
      .then((data) => {
        const newToken = data.jwtToken;
        setToken(newToken);
        refreshPromiseRef.current = null;
        return newToken;
      })
      .catch((error) => {
        refreshPromiseRef.current = null;
        setToken(null);
        throw error;
      });

    return refreshPromiseRef.current;
  }, []);

  useEffect(() => {
    postJson(`${AUTH_API_URL}/auth/public/refresh`)
      .then((data) => {
        const { jwtToken, roles } = data;
        setToken(jwtToken);
        setIsAdmin(roles && roles.includes('ROLE_ADMIN'));
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <ContextApi.Provider
      value={{
        token,
        setToken,
        logout,
        refreshAccessToken,
        currentUser,
        setCurrentUser,
        openSidebar,
        setOpenSidebar,
        isAdmin,
        setIsAdmin,
        loading,
      }}
    >
      {children}
    </ContextApi.Provider>
  );
};

export const useMyContext = () => {
  const context = useContext(ContextApi);
  if (!context) {
    throw new Error('useMyContext must be used within a ContextProvider');
  }
  return context;
};
